<template>
  <auth-layout>
    <div class="mb-6">
      <h2 class="xl:text-4xl xl:font-light text-2xl font-bold">
        Registration
      </h2>
      <p class="mt-2">
        <icon-link :href="route('login')" icon="arrow-thin-left-circle">
          Back to login
        </icon-link>
      </p>
    </div>
    <form novalidate @submit.prevent="register">
      <p class="text-xs italic">
        All fields are required for registration.
      </p>

      <auth-input
        v-model="auth.name"
        label="Name"
        input-type="text"
        :error="getError('name')"
        placeholder="Your Name"
        :loading="loading"
        :autocomplete="true"
      />

      <auth-input
        v-model="auth.job_title"
        label="Job Title"
        input-type="text"
        :error="getError('job_title')"
        placeholder="Your Job Title"
        :loading="loading"
        :autocomplete="true"
      />

      <auth-input
        v-model="auth.email"
        label="Email"
        input-type="email"
        :error="getError('email')"
        placeholder="e.g. your.name@email.com"
        :loading="loading"
        :autocomplete="true"
      />

      <auth-input
        v-model="auth.telephone"
        label="Telephone"
        input-type="tel"
        :error="getError('telephone')"
        placeholder="e.g. 02476 528 700"
        :loading="loading"
        :autocomplete="true"
      />

      <div class="pt-2 pb-6">
        <div class="text-center border-b border-grey-200">
          <p class="font-semibold -mb-3">
            <span class="bg-white px-3">Company Details</span>
          </p>
        </div>
      </div>

      <transition
        enter-active-class="transition-all ease-in duration-300"
        enter-class="max-h-0 opacity-0 bg-white"
        enter-to-class="max-h-80 opacity-100"
        leave-active-class="transition-all ease-out duration-200"
        leave-class="max-h-80 opacity-100"
        leave-to-class="max-h-0 bg-white opacity-0"
        mode="out-in"
      >
        <auth-input
          v-if="!auth.unregistered_organisation"
          v-model="auth.company_number"
          label="Company Number"
          input-type="text"
          :error="getError('company_number')"
          placeholder="01234567"
          :maxlength="8"
          :loading="loading"
          :autocomplete="true"
          @blur="companyPrefixZero"
        >
          <div
            v-if="hasCompany"
            class="bg-grey-100 -ml-2 -mr-2 -mb-2 mt-2 px-3 py-2 text-black font-light"
          >
            <h2 class="text-xs text-grey-500">
              Your company's details
            </h2>
            <p class="text-sm">
              {{ company_details.name | sentencecase }}
            </p>
            <p class="text-xs">
              {{ companyAddress }}
            </p>
          </div>
        </auth-input>
      </transition>

      <input-checkbox
        v-model="auth.unregistered_organisation"
        :value="true"
        input-type="checkbox"
        required
        class="mt-6 text-left"
      >
        I don't have a company number.
      </input-checkbox>

      <transition
        enter-active-class="transition-all ease-in duration-300"
        enter-class="max-h-0 opacity-0 bg-white"
        enter-to-class="max-h-screen opacity-100"
        leave-active-class="transition-all ease-out duration-200"
        leave-class="max-h-screen opacity-100"
        leave-to-class="max-h-0 bg-white opacity-0"
        mode="out-in"
      >
        <div v-if="auth.unregistered_organisation">
          <auth-input
            v-model="auth.company_name"
            class="mb-4 relative"
            label="Company Name"
            :error="getError('company_name')"
            input-type="text"
            :loading="loading"
          />
          <auth-input
            v-model="auth.address_line_1"
            class="mb-2"
            label="Address"
            :error="getError('address_line_1')"
            input-type="text"
            :loading="loading"
          />
          <auth-input
            v-model="auth.address_line_2"
            class="mb-4"
            :error="getError('address_line_2')"
            input-type="text"
            :loading="loading"
          />
          <auth-input
            v-model="auth.town"
            label="Town"
            class="mb-4"
            :error="getError('town')"
            input-type="text"
            :loading="loading"
          />
          <auth-input
            v-model="auth.county"
            label="County"
            class="mb-4"
            :error="getError('county')"
            input-type="text"
            :loading="loading"
          />
          <auth-input
            v-model="auth.postcode"
            label="Post Code"
            class="mb-4"
            :error="getError('postcode')"
            input-type="text"
            :loading="loading"
          />
        </div>
      </transition>

      <div class="pt-4 pb-6">
        <div class="text-center border-b border-grey-200">
          <p class="font-semibold -mb-3">
            <span class="bg-white px-3">Authentication</span>
          </p>
        </div>
      </div>

      <auth-input
        v-model="auth.password"
        label="Password"
        input-type="password"
        :error="getError('password')"
        placeholder="Your Password"
        :loading="loading"
        :autocomplete="true"
      />
      <auth-input
        v-model="auth.password_confirmation"
        label="Confirm Password"
        input-type="password"
        :error="getError('password_confirmation')"
        placeholder="Confirm Password"
        :loading="loading"
      />

      <input-checkbox
        v-model="auth.accept"
        :value="true"
        input-type="checkbox"
        required
        class="mt-6 text-left"
      >
        Please click here to confirm that you agree to the APC Funding Portal
        <a href="/terms-conditions" target="_blank">Terms and Conditions</a> and
        <a href="/privacy-policy" target="_blank">Privacy Policy</a>.
      </input-checkbox>

      <app-button
        type="submit"
        colour="primary"
        class="mt-8 mb-4"
        :disabled="!auth.accept || !hasCompany || loading"
      >
        <span v-if="!loading">
          <template v-if="!auth.accept">
            Please accept T&Cs
          </template>
          <template v-else-if="!hasCompany">
            Please provide company details
          </template>
          <template v-else>
            Register
          </template>
        </span>
        <span v-else>Registering...</span>
      </app-button>

      <p class="text-center">
        <a
          :href="
            `mailto:${$config.emails.technical}?subject=APC ${$config.name} Login issue`
          "
          class="icon-link underline"
        >
          Having problems registering?
        </a>
      </p>
    </form>
  </auth-layout>
</template>

<script>
import debounce from "lodash/debounce";
import pick from "lodash/pick";
import { requestMixin } from "../mixins/request";

export default {
  name: "Register",
  mixins: [requestMixin],
  data() {
    return {
      auth: {
        name: "",
        job_title: "",
        email: "",
        telephone: "",
        password: "",
        password_confirmation: "",
        company_number: "",
        company_name: "",
        address_line_1: "",
        address_line_2: "",
        town: "",
        county: "",
        postcode: "",
        unregistered_organisation: false,
        accept: false
      },
      company_details: {
        dirty: ""
      },
      loading: false
    };
  },
  computed: {
    hasCompany() {
      if (this.auth.unregistered_organisation) return true;
      return (
        Object.keys(this.company_details).length > 1 &&
        this.auth.company_number.length === 8
      );
    },
    companyAddress() {
      let address = pick(this.company_details, [
        "address_line_1",
        "address_line_2",
        "town",
        "county",
        "postcode"
      ]);
      return Object.values(address)
        .filter(Boolean)
        .join(", ");
    }
  },
  watch: {
    "auth.company_number": debounce(function() {
      return this.companySearch();
    }, 500)
  },
  created() {
    document.title = `Register :: ${document.title}`;
  },
  methods: {
    register() {
      this.loading = true;
      return axios
        .post(route("register"), this.auth)
        .then(response => this.registerResponse(response))
        .catch(error => this.requestError(error))
        .then(() => (this.loading = false));
    },
    registerResponse() {
      Turbolinks.visit(route("login"));
    },
    companyPrefixZero() {
      if (this.auth.company_number.length === 7)
        return (this.auth.company_number = 0 + this.auth.company_number + "");
    },
    companySearch() {
      this.removeError("company_number");
      if (this.auth.company_number.length !== 8) return;
      if (this.auth.company_number === this.company_details.dirty) return;
      this.company_details = pick(this.company_details, ["dirty"]);
      this.company_details.dirty = this.auth.company_number;

      return axios
        .post(route("api.company-search"), pick(this.auth, "company_number"))
        .then(response => this.companySearchResponse(response))
        .catch(error => this.requestError(error));
    },
    companySearchResponse(response) {
      return (this.company_details = {
        ...this.company_details,
        ...response.data
      });
    }
  }
};
</script>

<style></style>
